import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'hooks/use-local-storage';
import { DEFAULT_QUICK_ENTRY_PREFS, QUICK_ENTRY_PREFS_KEY } from 'utils/quick-entry';
import { configureQuickEntryNative, getQuickEntryStatus } from 'utils/quick-entry-native';
import { isTauriApp } from 'utils/is-tauri';
import { isQuickEntryWindow } from 'utils/desktop-window';
/**
 * Persist Quick Entry prefs. Native sync runs only from the main shell
 * (`useSyncQuickEntryNative`) so we don't race unregister/register.
 */ export const useQuickEntryPrefs = ()=>{
    const [prefs, setPrefs] = useLocalStorage(QUICK_ENTRY_PREFS_KEY, DEFAULT_QUICK_ENTRY_PREFS);
    const updatePrefs = useCallback((patch)=>{
        setPrefs((prev)=>({
                ...prev,
                ...patch
            }));
    }, [
        setPrefs
    ]);
    return {
        prefs,
        setPrefs,
        updatePrefs
    };
};
/**
 * Push prefs to Rust global-shortcut layer. Call once from DesktopAppShell.
 */ export const useSyncQuickEntryNative = ()=>{
    const { prefs } = useQuickEntryPrefs();
    const [status, setStatus] = useState(null);
    const [syncError, setSyncError] = useState(null);
    const syncingRef = useRef(false);
    const refreshStatus = useCallback(async ()=>{
        const next = await getQuickEntryStatus();
        setStatus(next);
    }, []);
    useEffect(()=>{
        if (!isTauriApp() || isQuickEntryWindow()) {
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(()=>{
            if (syncingRef.current) {
                return;
            }
            syncingRef.current = true;
            configureQuickEntryNative(prefs).then(async ()=>{
                if (cancelled) {
                    return;
                }
                setSyncError(null);
                await refreshStatus();
            }).catch((error)=>{
                if (cancelled) {
                    return;
                }
                const message = error instanceof Error ? error.message : typeof error === 'string' ? error : 'Failed to register Quick Entry shortcut';
                setSyncError(message);
                void refreshStatus();
            }).finally(()=>{
                syncingRef.current = false;
            });
        }, 150);
        return ()=>{
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        prefs,
        refreshStatus
    ]);
    return {
        status,
        syncError,
        refreshStatus
    };
};
/** @deprecated Use useQuickEntryPrefs + useSyncQuickEntryNative */ export const useQuickEntryConfig = useQuickEntryPrefs;
